import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { Display, FontSizeRescalerService } from '@app/shared/services/font-rescaler.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { GoldenBomb } from '@shared/models/goldenBomb.model';
import { GoldenBombService } from '@shared/services/goldenBomb.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DecimalPipe } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { AudioService } from '@app/audio/audio.service';
import { GoldenBombRepository } from '@app/shared/state/golden-bomb/golden-bomb.repository';
import { ButtonComponent } from '../button/button.component';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-golden-bomb',
  templateUrl: './golden-bomb.component.html',
  styleUrls: ['./golden-bomb.component.scss'],
  standalone: true,
  imports: [ButtonComponent, DecimalPipe, SharedModule],
})
export class GoldenBombComponent implements OnInit, OnDestroy {
  goldenBomb = signal<GoldenBomb | null>(null);
  currentBonus = signal(0);
  loading = signal(false);

  private destroySubject$: Subject<void> = new Subject();

  private goldenBombService = inject(GoldenBombService);
  private audioService = inject(AudioService);
  public logger = inject(LoggerService);
  private goldenBombRepo = inject(GoldenBombRepository);
  private eventManager = inject(EventManager);
  private fontSizeRescalerService = inject(FontSizeRescalerService);

  async ngOnInit() {
    await this.goldenBombService.init();

    this.goldenBombService.currentBonus$.pipe(takeUntil(this.destroySubject$)).subscribe({
      next: (currentBonus) => {
        this.currentBonus.set(currentBonus);
        this.setWidthLoadingBar(currentBonus);

        this.loading.set(currentBonus <= 1);
      },
      error: (error) => this.logger.error(error),
    });

    this.goldenBombRepo.goldenBomb$.pipe(takeUntil(this.destroySubject$)).subscribe({
      next: (goldenBomb) => {
        this.goldenBomb.set(goldenBomb);

        if (goldenBomb.canRedeem) {
          this.currentBonus.set(goldenBomb.totalBonus);
          this.setWidthLoadingBar(goldenBomb.totalBonus);
        }
      },
      error: (error) => this.logger.error(error),
    });
  }

  redeemGoldenBomb = async (): Promise<void> => {
    this.audioService.playButtonAudio('reward');
    this.loading.set(true);

    try {
      const goldenBomb = await this.goldenBombService.redeem();
      this.currentBonus.set(0);
      this.setWidthLoadingBar(0);
      goldenBomb.canRedeem = false;
      this.goldenBombRepo.updateGoldenBomb(goldenBomb);
      this.goldenBombService.createInterval(goldenBomb);
    } catch (err) {
      this.logger.error(err);
      this.goldenBombService.createInterval(this.goldenBomb()!);
    }
  };

  openInfo() {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.InfoGoldenBombModalComponent, ModalType.View, {
        totalBonus: this.goldenBomb()!.totalBonus,
      })
    );
  }

  private setWidthLoadingBar(currentBonus: number): void {
    const loadingBar = document.getElementById('loading-bar');

    if (loadingBar) {
      if (currentBonus === 0) {
        loadingBar.style.transition = 'none';
      } else {
        loadingBar.style.transition = 'width ' + this.goldenBomb()!.refreshSeconds + 's';
      }

      const percentage = (currentBonus * 100) / this.goldenBomb()!.totalBonus;
      loadingBar.style.width = 17 + percentage + '%';
    }
  }

  setFontSize(): string {
    return this.fontSizeRescalerService.setFontSize(Display.GoldenBomb, this.currentBonus());
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.unsubscribe();
  }
}
